import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AbstractSubmissionClosed, { useAbstractDeadline } from './AbstractSubmissionClosed';

const CustomEventAbstractUndergrad = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        college: '',
        state: '',
        guideName: '',
        guideDesignation: '',
        guideCollege: '',
        guideState: '',
        guideConfId: ''
    });

    const [verifying, setVerifying] = useState(false);
    const [verifiedGuide, setVerifiedGuide] = useState(null);
    const [verificationError, setVerificationError] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [abstractDoc, setAbstractDoc] = useState(null);
    const { deadlinePassed, checking } = useAbstractDeadline();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            if (name === 'guideConfId') {
                setVerifiedGuide(null);
                setVerificationError('');
            }
            return { ...prev, [name]: name === 'guideConfId' ? value.replace(/\s/g, '').toUpperCase() : value };
        });
    };

    const verifyGuideId = async () => {
        if (!formData.guideConfId) return;
        setVerifying(true);
        setVerificationError('');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/verify-conference/${formData.guideConfId}?type=abstract`);
            const data = await res.json();
            if (data.success && data.data) {
                const guideUser = data.data;
                setVerifiedGuide(guideUser);
                setFormData(prev => ({
                    ...prev,
                    guideName: guideUser.fullName || guideUser.name || '',
                    guideDesignation: guideUser.designation || '',
                    guideCollege: guideUser.organization || guideUser.college || guideUser.institute || '',
                    guideState: guideUser.state || ''
                }));
            } else {
                setVerificationError(data.message || 'Invalid Guide Registration ID or not found.');
                setVerifiedGuide(null);
            }
        } catch (err) {
            setVerificationError('Error verifying ID');
            setVerifiedGuide(null);
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!verifiedGuide) {
            alert('Please verify the Guide Conference Registration ID before submitting.');
            return;
        }

        const requiredFields = ['name', 'college', 'state', 'guideName', 'guideDesignation', 'guideCollege', 'guideState', 'guideConfId'];
        for (const field of requiredFields) {
            if (!formData[field]) {
                alert(`Please fill the required field: ${field}`);
                return;
            }
        }

        if (!abstractDoc) {
            alert('Please upload an abstract document (PDF/Word).');
            return;
        }

        setLoading(true);
        try {
            const submitData = new FormData();
            Object.keys(formData).forEach(key => submitData.append(key, formData[key]));
            submitData.append('abstractDoc', abstractDoc);

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/abstract/undergrad/register`, {
                method: 'POST',
                body: submitData
            });
            const regData = await res.json();

            if (regData.success) {
                setSubmitted(true);
            } else {
                alert(regData.message || 'Submission failed');
            }
        } catch (error) {
            console.error(error);
            alert('An error occurred during submission');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#fcfaff] p-4 text-center">
                <div className="bg-white p-10 rounded-3xl shadow-xl max-w-lg w-full">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <span className="text-4xl">✅</span>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-4 font-sans tracking-tight">Submission Successful!</h2>
                    <p className="text-gray-600 mb-8 max-w-sm mx-auto">Your undergraduate abstract has been recorded. Thank you for your submission.</p>
                    <button onClick={() => navigate('/event/jss/conference')}
                        className="bg-[#5928ed] hover:bg-[#4013b5] text-white px-8 py-3 rounded-full font-bold tracking-wide transition-all w-full">
                        Return to Event Page
                    </button>
                </div>
            </div>
        );
    }

    const inputClasses = "w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm font-medium transition-all duration-200 outline-none focus:border-[#5928ed] focus:ring-4 focus:ring-[#5928ed]/10 placeholder-gray-300";

    if (checking) return null;
    if (deadlinePassed) return <AbstractSubmissionClosed />;

    return (
        <div className="min-h-screen bg-[#fcfaff] py-12 px-4 sm:px-6 font-sans">
            <div className="max-w-3xl mx-auto space-y-8">

                {/* Header */}
                <div className="bg-gradient-to-br from-[#4013b5] to-[#7244f7] rounded-3xl p-8 sm:p-12 text-center text-white shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="relative z-10">
                        <span className="uppercase tracking-[0.2em] font-bold text-xs opacity-80 bg-white/20 px-4 py-1.5 rounded-full inline-block mb-4">Abstract Submission - Oral Paper</span>
                        <h1 className="text-3xl sm:text-4xl font-black mb-3 tracking-tight">Undergraduate Category</h1>
                        <p className="text-purple-100 text-lg max-w-xl mx-auto font-medium">Please provide your details and your guide's verified conference registration ID.</p>
                    </div>
                </div>

                {/* Form Wrapper */}
                <div className="bg-white rounded-3xl shadow-xl border border-gray-50 overflow-hidden">
                    <div className="bg-gray-50 px-8 py-5 border-b border-gray-100 flex items-center gap-3">
                        <span className="text-2xl">📝</span>
                        <h2 className="text-xl font-bold text-gray-800">Submission Details</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-8">

                        {/* Guide Verification */}
                        <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100/50 space-y-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-blue-500">🛡️</span>
                                <label className="text-sm font-bold text-blue-900 uppercase tracking-wider">Guide Conference Reg ID *</label>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <input
                                    type="text"
                                    name="guideConfId"
                                    value={formData.guideConfId}
                                    onChange={handleChange}
                                    placeholder="Ex: KAP-XXXX"
                                    disabled={verifiedGuide || verifying}
                                    className="flex-1 bg-white border border-blue-200 rounded-xl px-4 py-3 text-gray-800 font-bold tracking-wider outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-400/20 uppercase"
                                />
                                {!verifiedGuide ? (
                                    <button
                                        type="button"
                                        onClick={verifyGuideId}
                                        disabled={!formData.guideConfId || verifying}
                                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-8 py-3 rounded-xl font-bold tracking-wide transition-all shadow-md active:scale-95"
                                    >
                                        {verifying ? 'Verifying...' : 'Verify Guide'}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => handleChange({ target: { name: 'guideConfId', value: '' } })}
                                        className="bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 px-8 py-3 rounded-xl font-bold tracking-wide transition-all"
                                    >
                                        Change
                                    </button>
                                )}
                            </div>
                            {verificationError && <p className="text-red-500 text-sm font-bold bg-red-50 p-2 rounded-lg inline-block px-3">{verificationError}</p>}
                            {verifiedGuide && (
                                <div className="mt-6 bg-white border border-green-200 p-6 sm:p-8 rounded-3xl shadow flex flex-col sm:flex-row gap-6 items-start animate-fade-in relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-full blur-3xl opacity-60 -mr-10 -mt-10 pointer-events-none"></div>
                                    {/* Photo Section */}
                                    {(verifiedGuide.photo_url || verifiedGuide.photoUrl) ? (
                                        <div className="shrink-0 w-32 h-32 sm:w-40 sm:h-40 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 shadow-sm relative z-10">
                                            <img src={verifiedGuide.photo_url || verifiedGuide.photoUrl} alt="Guide" className="w-full h-full object-cover" />
                                        </div>
                                    ) : (
                                        <div className="shrink-0 w-32 h-32 sm:w-40 sm:h-40 bg-green-50 rounded-xl flex items-center justify-center border border-green-100 shadow-sm relative z-10">
                                            <span className="text-4xl text-green-500">✅</span>
                                        </div>
                                    )}

                                    {/* Data Section */}
                                    <div className="flex-1 w-full space-y-4 relative z-10">
                                        <div className="border-b border-gray-100 pb-3">
                                            <h3 className="text-2xl font-bold text-gray-900">{verifiedGuide.fullName || verifiedGuide.name}</h3>
                                            <p className="text-sm font-semibold text-[#5928ed]">{verifiedGuide.registrationCategory || verifiedGuide.category || 'Verified Guide'}</p>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-4">
                                            {[
                                                { label: 'Registration ID', value: verifiedGuide.registrationId || verifiedGuide.confId },
                                                { label: 'Email', value: verifiedGuide.email },
                                                { label: 'Contact Number', value: verifiedGuide.contactNumber },
                                                { label: 'Registration Date', value: verifiedGuide.createdAt ? new Date(verifiedGuide.createdAt).toLocaleDateString('en-GB') : null },
                                                { label: 'Organization', value: verifiedGuide.organization || verifiedGuide.college || verifiedGuide.institute },
                                                { label: 'Designation', value: verifiedGuide.designation },
                                                { label: 'City', value: verifiedGuide.city },
                                                { label: 'State', value: verifiedGuide.state }
                                            ].map((field, idx) => field.value && field.value !== 'null' && field.value !== 'undefined' ? (
                                                <div key={idx}>
                                                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">{field.label}</p>
                                                    <p className="text-gray-900 text-sm font-semibold truncate" title={String(field.value)}>{String(field.value)}</p>
                                                </div>
                                            ) : null)}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Student Details */}
                        <div className="space-y-6">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2">Student Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Student Name *</label>
                                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClasses} placeholder="Enter full name" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">College *</label>
                                    <input type="text" name="college" value={formData.college} onChange={handleChange} required className={inputClasses} placeholder="Enter college name" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">State *</label>
                                    <input type="text" name="state" value={formData.state} onChange={handleChange} required className={inputClasses} placeholder="Enter state" />
                                </div>
                            </div>
                        </div>

                        {/* Guide Details */}
                        <div className="space-y-6">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2">Guide Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Guide Name *</label>
                                    <input type="text" name="guideName" value={formData.guideName} onChange={handleChange} required className={inputClasses} placeholder="Enter guide name" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Guide Designation *</label>
                                    <input type="text" name="guideDesignation" value={formData.guideDesignation} onChange={handleChange} required className={inputClasses} placeholder="Ex: Professor" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Guide College *</label>
                                    <input type="text" name="guideCollege" value={formData.guideCollege} onChange={handleChange} required className={inputClasses} placeholder="Enter guide's college" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Guide State *</label>
                                    <input type="text" name="guideState" value={formData.guideState} onChange={handleChange} required className={inputClasses} placeholder="Enter guide's state" />
                                </div>
                            </div>
                        </div>

                        {/* Abstract Upload */}
                        <div className="space-y-4">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2">Abstract Upload</h3>
                            <div className="space-y-2">
                                <label className="text-[11px] font-bold text-[#5928ed] uppercase tracking-[0.15em] ml-1">Upload Abstract (PDF/MSWord) *</label>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => setAbstractDoc(e.target.files[0])}
                                    required
                                    className="w-full bg-white border border-dashed border-[#5928ed]/40 hover:border-[#5928ed] rounded-xl px-4 py-6 text-gray-800 text-sm font-medium transition-all duration-200 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-[#fcfaff] file:text-[#5928ed] cursor-pointer"
                                />
                                <p className="text-[10px] text-gray-400 ml-1 font-medium mt-1">Maximum file size: 5MB</p>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-6">
                            <button
                                type="submit"
                                disabled={loading || !verifiedGuide}
                                className={`w-full py-4 rounded-xl font-bold tracking-widest uppercase transition-all shadow-lg text-white
                                    ${loading || !verifiedGuide ? 'bg-gray-300 shadow-none cursor-not-allowed' : 'bg-gradient-to-r from-[#5928ed] to-[#7244f7] hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0'}`}
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default CustomEventAbstractUndergrad;
